import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/shared/Layout';
import { adminAPI, employeeAPI, vacationAPI, departmentAPI } from '../services/api';
import { useConfirm } from '../components/shared/ConfirmDialog';
import { format } from 'date-fns';
import {
  Plus, Pencil, Play, KeyRound,
  Trash2, X, Info, ShieldCheck,
  Cake, Star, Gift,
} from 'lucide-react';
import toast from 'react-hot-toast';
import TodayAbsences  from '../components/shared/TodayAbsences';

function useActiveTab() {
  const { pathname } = useLocation();
  if (pathname.includes('/empleados'))      return 'employees';
  if (pathname.includes('/supervisores'))   return 'supervisors';
  if (pathname.includes('/vacaciones'))     return 'vacations';
  if (pathname.includes('/departamentos'))  return 'departments';
  if (pathname.includes('/reportes'))       return 'reports';
  if (pathname.includes('/acumulaciones'))   return 'accruals';
  return 'overview';
}

function SectionLabel({ children, top }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 700, letterSpacing: '0.07em',
      color: 'var(--gray-400)', textTransform: 'uppercase',
      marginBottom: 10, marginTop: top ? 20 : 0,
    }}>{children}</div>
  );
}

function DepartmentSelect({ value, onChange, departments }) {
  return (
    <select className="form-input form-select" value={value} onChange={onChange}>
      <option value="">— Sin departamento —</option>
      {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
    </select>
  );
}

// Calcular años de antigüedad en el frontend para el preview
function calcVacationPreview(hireDate) {
  if (!hireDate) return { years: null, days: 0, label: '' };
  const hire = new Date(hireDate);
  const today = new Date();
  let years = today.getFullYear() - hire.getFullYear();
  const m = today.getMonth() - hire.getMonth();
  const d = today.getDate() - hire.getDate();
  if (m < 0 || (m === 0 && d < 0)) years--;
  if (years < 0) years = 0;

  let days = 0, label = '';
  if (years < 1)       { days = 0;  label = 'Sin derecho aún (menos de 1 año)'; }
  else if (years <= 5) { days = 12; label = `${years} año(s) → 12 días hábiles`; }
  else if (years <= 10){ days = 18; label = `${years} año(s) → 18 días hábiles`; }
  else                 { days = 30; label = `${years} año(s) → 30 días hábiles`; }
  return { years, days, label };
}

// ── Modal empleado ───────────────────────────────────────────────────────────
function EmployeeModal({ employee, supervisors, departments, onClose, onDone }) {
  const isEdit = !!employee;
  const [form, setForm] = useState(employee ? {
    first_name: employee.first_name || '',
    last_name: employee.last_name || '',
    birth_date: employee.birth_date ? employee.birth_date.split('T')[0] : '',
    department_id: employee.department_id || '',
    position: employee.position || '',
    hire_date: employee.hire_date ? employee.hire_date.split('T')[0] : '',
    vacation_days_total: employee.vacation_days_total ?? 0,
    vacation_days_override: employee.vacation_days_override ?? false,
    phone_whatsapp: employee.phone_whatsapp || '',
    document_number: employee.document_number || '',
    supervisor_ids: employee.supervisor_ids || [],
    is_active: employee.is_active ?? true,
    email: employee.email || '',
    password: '',
  } : {
    email: '', password: '', first_name: '', last_name: '',
    birth_date: '', department: '', position: '',
    hire_date: '', vacation_days_total: 0,
    vacation_days_override: false, phone_whatsapp: '', supervisor_ids: [],
  });
  const [loading, setLoading] = useState(false);

  const f = (key) => (e) => setForm(p => ({ ...p, [key]: e.target.value }));
  const preview = calcVacationPreview(form.hire_date);

  const toggleSup = (id) => setForm(p => ({
    ...p,
    supervisor_ids: p.supervisor_ids.includes(id)
      ? p.supervisor_ids.filter(s => s !== id)
      : [...p.supervisor_ids, id],
  }));

  // Cuando cambia hire_date y no hay override, actualizar preview automáticamente
  const handleHireDateChange = (e) => {
    const newDate = e.target.value;
    const prev = calcVacationPreview(newDate);
    setForm(p => ({
      ...p,
      hire_date: newDate,
      // Solo actualizar vacation_days_total si no es override
      ...(!p.vacation_days_override && { vacation_days_total: prev.days }),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEdit && form.supervisor_ids.length === 0) {
      toast.error('Debes asignar al menos un supervisor al empleado');
      return;
    }
    setLoading(true);
    try {
      if (isEdit) {
        await employeeAPI.update(employee.id, form);
        toast.success('Empleado actualizado');
      } else {
        await employeeAPI.create(form);
        toast.success('Empleado creado');
      }
      onDone(); onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 660 }}>
        <div className="modal-header">
          <div className="modal-title">{isEdit ? 'Editar empleado' : 'Nuevo empleado'}</div>
          <button className="modal-close" onClick={onClose}><X size={18}/></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ maxHeight: '72vh', overflowY: 'auto' }}>

            <SectionLabel>Datos personales</SectionLabel>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Nombre *</label>
                <input className="form-input" value={form.first_name} onChange={f('first_name')} required />
              </div>
              <div className="form-group">
                <label className="form-label">Apellido *</label>
                <input className="form-input" value={form.last_name} onChange={f('last_name')} required />
              </div>
              <div className="form-group">
                <label className="form-label">N° de documento *</label>
                <input className="form-input" value={form.document_number} onChange={f('document_number')} required />
              </div>
              <div className="form-group">
                <label className="form-label">Fecha de nacimiento *</label>
                <input type="date" className="form-input" value={form.birth_date} onChange={f('birth_date')} required />
              </div>
              <div className="form-group">
                <label className="form-label">WhatsApp</label>
                <input className="form-input" placeholder="+595 981 000000" value={form.phone_whatsapp} onChange={f('phone_whatsapp')} />
              </div>
            </div>

            <SectionLabel top>Datos laborales</SectionLabel>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Departamento</label>
                <DepartmentSelect value={form.department_id} onChange={f('department_id')} departments={departments} />
              </div>
              <div className="form-group">
                <label className="form-label">Cargo</label>
                <input className="form-input" value={form.position} onChange={f('position')} />
              </div>
              {isEdit && (
                <div className="form-group">
                  <label className="form-label">Estado</label>
                  <select className="form-input form-select"
                    value={form.is_active ? 'true' : 'false'}
                    onChange={e => setForm(p => ({ ...p, is_active: e.target.value === 'true' }))}>
                    <option value="true">✅ Activo</option>
                    <option value="false">❌ Inactivo</option>
                  </select>
                </div>
              )}
            </div>

            {/* Fecha de ingreso + vacaciones */}
            <SectionLabel top>Vacaciones</SectionLabel>
            <div className="form-group">
              <label className="form-label">Fecha de ingreso *</label>
              <input type="date" className="form-input" value={form.hire_date}
                onChange={handleHireDateChange}
                style={{ maxWidth: 220 }} required />
            </div>

            {/* Preview automático */}
            {form.hire_date && !form.vacation_days_override && (
              <div style={{
                background: preview.days > 0 ? 'var(--success-light)' : 'var(--warning-light)',
                border: `1px solid ${preview.days > 0 ? '#bbf7d0' : '#fde68a'}`,
                borderRadius: 8, padding: '10px 14px', marginBottom: 16,
                fontSize: 13, color: preview.days > 0 ? 'var(--success)' : 'var(--warning)',
              }}>
                📅 <strong>Antigüedad:</strong> {preview.label}
                {preview.days > 0 && (
                  <span style={{ marginLeft: 8, fontWeight: 600 }}>
                    → Se asignarán <strong>{preview.days}</strong> días de vacaciones
                  </span>
                )}
              </div>
            )}

            {/* Toggle override */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <input
                type="checkbox"
                id="override-check"
                checked={form.vacation_days_override}
                onChange={e => setForm(p => ({ ...p, vacation_days_override: e.target.checked }))}
                style={{ width: 16, height: 16, cursor: 'pointer' }}
              />
              <label htmlFor="override-check" style={{ fontSize: 13, cursor: 'pointer', color: 'var(--gray-700)' }}>
                Editar días manualmente (sobrescribe el cálculo automático)
              </label>
            </div>

            {/* Campo manual solo si override */}
            {form.vacation_days_override && (
              <div className="form-group" style={{ maxWidth: 220 }}>
                <label className="form-label">Días de vacaciones *</label>
                <input type="number" className="form-input" min="0" max="365"
                  value={form.vacation_days_total}
                  onChange={e => setForm(p => ({ ...p, vacation_days_total: parseInt(e.target.value) || 0 }))}
                  required />
                <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4 }}>
                  El cálculo automático no se aplicará a este empleado
                </div>
              </div>
            )}

            {/* Resumen actual si es edición */}
            {isEdit && (
              <div style={{ background: 'var(--gray-50)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--gray-600)', marginBottom: 8 }}>
                Días actuales: <strong>{employee.vacation_days_total}</strong> total ·{' '}
                <strong>{employee.vacation_days_used}</strong> usados ·{' '}
                <strong style={{ color: 'var(--primary)' }}>{employee.vacation_days_total - employee.vacation_days_used}</strong> disponibles
              </div>
            )}

            {!isEdit && (
              <>
                <SectionLabel top>Acceso al sistema</SectionLabel>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input type="email" className="form-input" value={form.email} onChange={f('email')} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Contraseña *</label>
                    <input type="password" className="form-input" placeholder="Mín. 6 caracteres"
                      value={form.password} onChange={f('password')} minLength={6} required />
                  </div>
                </div>
              </>
            )}

            <SectionLabel top>Supervisores asignados {!isEdit && <span style={{color:'var(--danger)',fontSize:11}}> *</span>}</SectionLabel>
            {supervisors.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>No hay supervisores registrados.</div>
            ) : (
              <>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {supervisors.map(s => {
                    const selected = form.supervisor_ids.includes(s.id);
                    return (
                      <button key={s.id} type="button" onClick={() => toggleSup(s.id)} style={{
                        padding: '7px 14px', borderRadius: 8, cursor: 'pointer',
                        fontFamily: 'var(--font)', fontSize: 13, fontWeight: 500,
                        border: selected ? '2px solid var(--primary)' : '2px solid var(--gray-200)',
                        background: selected ? 'var(--primary-light)' : 'white',
                        color: selected ? 'var(--primary)' : 'var(--gray-700)',
                        transition: 'all 0.15s',
                      }}>
                        {selected ? '' : ''}{s.first_name} {s.last_name}
                        {s.department && <span style={{ opacity: 0.6, fontSize: 12 }}> · {s.department}</span>}
                      </button>
                    );
                  })}
                </div>
                {form.supervisor_ids.length === 0 && !isEdit && (
                  <div style={{ marginTop: 8, fontSize: 12, color: 'var(--danger)', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Info size={12} /> Debes seleccionar al menos un supervisor
                  </div>
                )}
                {form.supervisor_ids.length > 0 && (
                  <div style={{ marginTop: 8, fontSize: 12, color: 'var(--gray-500)' }}>
                    El primero seleccionado es el supervisor principal.
                  </div>
                )}
              </>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Crear empleado'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Modal supervisor ─────────────────────────────────────────────────────────
function DepartmentModal({ dept, onClose, onDone }) {
  const confirm = useConfirm();
  const isEdit = !!dept;
  const [form, setForm] = useState({ name: dept?.name || '', description: dept?.description || '', is_active: dept?.is_active ?? true });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      if (isEdit) { await departmentAPI.update(dept.id, form); toast.success('Departamento actualizado'); }
      else        { await departmentAPI.create(form);           toast.success('Departamento creado');      }
      onDone(); onClose();
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
    finally { setLoading(false); }
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: `¿Eliminar "${dept.name}"?`,
      message: 'Esta acción no se puede deshacer. El departamento será eliminado permanentemente.',
      variant: 'danger',
      confirmLabel: 'Sí, eliminar',
    });
    if (!ok) return;
    setLoading(true);
    try { await departmentAPI.remove(dept.id); toast.success('Eliminado'); onDone(); onClose(); }
    catch (err) { toast.error(err.response?.data?.error || 'No se pudo eliminar'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">{isEdit ? 'Editar departamento' : 'Nuevo departamento'}</div>
          <button className="modal-close" onClick={onClose}><X size={18}/></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group"><label className="form-label">Nombre *</label>
              <input className="form-input" value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Ej: Tecnología, RRHH..." required /></div>
            <div className="form-group"><label className="form-label">Descripción</label>
              <textarea className="form-input form-textarea" value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></div>
            {isEdit && (
              <div className="form-group"><label className="form-label">Estado</label>
                <select className="form-input form-select" value={form.is_active ? 'true' : 'false'}
                  onChange={e => setForm(f => ({ ...f, is_active: e.target.value === 'true' }))}>
                  <option value="true">✅ Activo</option>
                  <option value="false">❌ Inactivo (no aparece en formularios)</option>
                </select></div>
            )}
          </div>
          <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
            <div>{isEdit && <button type="button" className="btn btn-danger btn-sm" onClick={handleDelete} disabled={loading} style={{display:"flex",alignItems:"center",gap:5}}><Trash2 size={13}/>Eliminar</button>}</div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
              <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Guardando…' : isEdit ? 'Guardar' : 'Crear'}</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Página principal ─────────────────────────────────────────────────────────
export default function AdminPage() {
  const navigate = useNavigate();
  const confirm  = useConfirm();
  const tab = useActiveTab();

  const [stats, setStats] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [birthdays, setBirthdays] = useState([]);
  const [allVacations, setAllVacations] = useState([]);
  const [allAccruals,   setAllAccruals]   = useState([]);
  const [birthdays2,    setBirthdays2]    = useState([]);
  const [anniversaries,  setAnniversaries]  = useState([]);
  const [anniversaries2, setAnniversaries2] = useState([]);
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [empModal, setEmpModal]   = useState(null);
  const [deptModal, setDeptModal] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    const [sRes, eRes, supRes, dRes, bRes, vRes, acRes] = await Promise.all([
      adminAPI.getStats().catch(() => ({ data: {} })),
      employeeAPI.getAll().catch(() => ({ data: [] })),
      adminAPI.getSupervisors().catch(() => ({ data: [] })),
      departmentAPI.getAll().catch(() => ({ data: [] })),
      adminAPI.getBirthdays().catch(() => ({ data: [] })),
      vacationAPI.getAll().catch(() => ({ data: [] })),
      adminAPI.getAllAccruals().catch(() => ({ data: [] })),
    ]);

    // Cargar también mes siguiente para cumpleaños y aniversarios
    const today = new Date();
    const nextMonth = today.getMonth() === 11 ? 1 : today.getMonth() + 2; // +2 porque getMonth es 0-based
    const [b2Res, aRes, a2Res] = await Promise.all([
      adminAPI.getBirthdays({ month: nextMonth }).catch(() => ({ data: [] })),
      adminAPI.getAnniversaries({ month: today.getMonth() + 1 }).catch(() => ({ data: [] })),
      adminAPI.getAnniversaries({ month: nextMonth }).catch(() => ({ data: [] })),
    ]);
    setBirthdays2(b2Res.data);
    setAnniversaries(aRes.data);
    setAnniversaries2(a2Res.data);
    setStats(sRes.data); setEmployees(eRes.data); setSupervisors(supRes.data);
    setDepartments(dRes.data); setBirthdays(bRes.data); setAllVacations(vRes.data);
    setAllAccruals(acRes.data);
    setLoading(false);
  };

  const handleEditEmployee = async (emp) => {
    try { const res = await employeeAPI.getById(emp.id); setEmpModal(res.data); }
    catch { toast.error('Error al cargar datos'); }
  };

  const handlePromote = async (emp) => {
    const ok = await confirm({
      title: `¿Promover a ${emp.first_name} ${emp.last_name}?`,
      message: 'Su rol cambiará a supervisor. Podrá aprobar vacaciones y permisos de los empleados asignados.',
      variant: 'confirm',
      confirmLabel: 'Sí, promover',
    });
    if (!ok) return;
    try {
      const res = await adminAPI.promoteToSupervisor(emp.id);
      toast.success(res.data.message);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al promover');
    }
  };

  const handleResetPassword = async (emp) => {
    const ok = await confirm({
      title: `¿Restablecer contraseña de ${emp.first_name} ${emp.last_name}?`,
      message: 'La contraseña se cambiará a Mega.613. El empleado deberá cambiarla luego.',
      variant: 'warning',
      confirmLabel: 'Sí, restablecer',
    });
    if (!ok) return;
    try {
      const res = await employeeAPI.resetPassword(emp.id);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al restablecer');
    }
  };

  const handleTriggerAnniversaries = async () => {
    const ok = await confirm({
      title: '¿Procesar aniversarios ahora?',
      message: 'Se acumularán días de vacaciones a todos los empleados que cumplan años de trabajo hoy.',
      variant: 'confirm',
      confirmLabel: 'Sí, procesar',
    });
    if (!ok) return;
    setTriggerLoading(true);
    try {
      await adminAPI.triggerAnniversaries();
      toast.success('Aniversarios procesados correctamente');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al procesar aniversarios');
    } finally {
      setTriggerLoading(false);
    }
  };

  const tabStyle = (t) => ({
    padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontFamily: 'var(--font)',
    fontWeight: 500, fontSize: 14, border: 'none', transition: 'all 0.15s',
    background: tab === t ? 'var(--gray-900)' : 'var(--gray-100)',
    color: tab === t ? 'white' : 'var(--gray-700)',
    display: 'flex', alignItems: 'center', gap: 6,
  });

  const statusBadge = (s) => <span className={`badge badge-${s}`}>{
    s === 'pending' ? 'Pendiente' : s === 'approved' ? 'Aprobado' :
    s === 'rejected' ? 'Rechazado' : 'Cancelado'
  }</span>;

  const activeDepts = departments.filter(d => d.is_active);
  const monthNames = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

  const antiquityLabel = (emp) => {
    if (!emp.vacation_info || !emp.hire_date) return '—';
    const { years, months } = emp.vacation_info;
    if (years === 0 && months === 0) return 'Reciente';
    if (years === 0) return `${months}m`;
    return `${years}a ${months}m`;
  };

  return (
    <Layout title="Panel de administrador">
      <div className="page-header">
        <div>
          <div className="page-title">Panel de administrador</div>
          <div className="page-subtitle">Gestión general del sistema</div>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {tab === 'accruals' && (
            <button className="btn btn-secondary" onClick={handleTriggerAnniversaries} disabled={triggerLoading} style={{display:'flex',alignItems:'center',gap:6}}>
              <Play size={14}/>{triggerLoading ? 'Procesando…' : 'Procesar aniversarios'}
            </button>
          )}
          {tab === 'departments' && <button className="btn btn-primary" onClick={() => setDeptModal('create')} style={{display:'flex',alignItems:'center',gap:6}}><Plus size={15}/>Nuevo departamento</button>}
          {(tab === 'employees' || tab === 'overview') && <button className="btn btn-primary" onClick={() => setEmpModal('create')} style={{display:'flex',alignItems:'center',gap:6}}><Plus size={15}/>Nuevo empleado</button>}
        </div>
      </div>

      {loading && <div style={{ padding: 32, textAlign: 'center', color: 'var(--gray-500)' }}>Cargando…</div>}

      {/* Overview */}
      {!loading && tab === 'overview' && (
        <>
          <div className="stats-grid">
            <div className="stat-card blue"><div className="stat-value">{stats?.totalEmployees ?? '—'}</div><div className="stat-label">Empleados activos</div></div>
            <div className="stat-card yellow"><div className="stat-value">{stats?.pendingVacations ?? '—'}</div><div className="stat-label">Vacaciones pendientes</div></div>
            <div className="stat-card gray"><div className="stat-value">{stats?.pendingLeaves ?? '—'}</div><div className="stat-label">Permisos pendientes</div></div>
            <div className="stat-card green"><div className="stat-value">{stats?.approvedThisMonth ?? '—'}</div><div className="stat-label">Aprobadas este mes</div></div>
          </div>

          
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Fila 1: Cumpleaños */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

            {/* Cumpleaños mes actual */}
            <div className="card">
              <div className="card-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Cake size={18} color="var(--warning)" />
                  <div>
                    <div className="card-title">Cumpleaños este mes</div>
                    <div className="card-subtitle">{monthNames[new Date().getMonth()]} {new Date().getFullYear()}</div>
                  </div>
                </div>
                <span className="badge badge-info">{birthdays.length}</span>
              </div>
              {birthdays.length === 0 ? (
                <div className="empty-state" style={{ padding: '20px 0' }}>
                  <div style={{ fontSize: 13, color: 'var(--gray-400)' }}>Sin cumpleaños este mes</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {birthdays.map((b, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'var(--warning-light)', borderRadius: 8 }}>
                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--warning)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Gift size={15} color="white" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{b.first_name} {b.last_name}</div>
                        <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{b.department || '—'}</div>
                      </div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--warning)' }}>Día {parseInt(b.birth_day)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Cumpleaños mes siguiente */}
            <div className="card">
              <div className="card-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Cake size={18} color="var(--gray-400)" />
                  <div>
                    <div className="card-title">Cumpleaños mes siguiente</div>
                    <div className="card-subtitle">{monthNames[new Date().getMonth() === 11 ? 0 : new Date().getMonth() + 1]}</div>
                  </div>
                </div>
                <span className="badge badge-info">{birthdays2.length}</span>
              </div>
              {birthdays2.length === 0 ? (
                <div className="empty-state" style={{ padding: '20px 0' }}>
                  <div style={{ fontSize: 13, color: 'var(--gray-400)' }}>Sin cumpleaños el mes siguiente</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {birthdays2.map((b, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'var(--gray-50)', borderRadius: 8 }}>
                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--gray-300)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Gift size={15} color="white" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{b.first_name} {b.last_name}</div>
                        <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{b.department || '—'}</div>
                      </div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--gray-500)' }}>Día {parseInt(b.birth_day)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Fila 2: Aniversarios laborales */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

            {/* Aniversarios mes actual */}
            <div className="card">
              <div className="card-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Star size={18} color="var(--primary)" />
                  <div>
                    <div className="card-title">Aniversarios laborales este mes</div>
                    <div className="card-subtitle">{monthNames[new Date().getMonth()]} — se acumulan días de vacaciones</div>
                  </div>
                </div>
                <span className="badge badge-info">{anniversaries.length}</span>
              </div>
              {anniversaries.length === 0 ? (
                <div className="empty-state" style={{ padding: '20px 0' }}>
                  <div style={{ fontSize: 13, color: 'var(--gray-400)' }}>Sin aniversarios este mes</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {anniversaries.map((a, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'var(--primary-light)', borderRadius: 8 }}>
                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Star size={14} color="white" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{a.first_name} {a.last_name}</div>
                        <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{a.department || '—'} · Día {parseInt(a.anniversary_day)}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--primary)' }}>{a.years_completing} año{a.years_completing > 1 ? 's' : ''}</div>
                        <div style={{ fontSize: 11, color: 'var(--success)', fontWeight: 600 }}>+{a.days_granted}d</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Aniversarios mes siguiente */}
            <div className="card">
              <div className="card-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Star size={18} color="var(--gray-400)" />
                  <div>
                    <div className="card-title">Aniversarios mes siguiente</div>
                    <div className="card-subtitle">{monthNames[new Date().getMonth() === 11 ? 0 : new Date().getMonth() + 1]}</div>
                  </div>
                </div>
                <span className="badge badge-info">{anniversaries2.length}</span>
              </div>
              {anniversaries2.length === 0 ? (
                <div className="empty-state" style={{ padding: '20px 0' }}>
                  <div style={{ fontSize: 13, color: 'var(--gray-400)' }}>Sin aniversarios el mes siguiente</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {anniversaries2.map((a, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'var(--gray-50)', borderRadius: 8 }}>
                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--gray-300)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Star size={14} color="white" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{a.first_name} {a.last_name}</div>
                        <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{a.department || '—'} · Día {parseInt(a.anniversary_day)}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--gray-600)' }}>{a.years_completing} año{a.years_completing > 1 ? 's' : ''}</div>
                        <div style={{ fontSize: 11, color: 'var(--gray-400)', fontWeight: 600 }}>+{a.days_granted}d</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

        </div>
      
          


        </>
      )}

      {/* Empleados */}
      {!loading && tab === 'employees' && (
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">Empleados ({employees.length})</div>
              <div className="card-subtitle">🔒 = días editados manualmente · 🤖 = calculado automáticamente</div>
            </div>
          </div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr><th>Nombre</th><th>Depto.</th><th>Ingreso</th><th>Antigüedad</th><th>Días vac.</th><th>Usados</th><th>Disponibles</th><th>Estado</th><th>Acciones</th></tr>
              </thead>
              <tbody>
                {employees.map(emp => (
                  <tr key={emp.id}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{emp.first_name} {emp.last_name}</div>
                      <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{emp.position || emp.email}</div>
                    </td>
                    <td>{emp.department || '—'}</td>
                    <td style={{ fontSize: 13 }}>
                      {emp.hire_date ? format(new Date(emp.hire_date), 'dd/MM/yyyy') : <span style={{ color: 'var(--gray-400)' }}>Sin fecha</span>}
                    </td>
                    <td style={{ fontSize: 13, color: 'var(--gray-600)' }}>{antiquityLabel(emp)}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span title={emp.vacation_days_override ? 'Editado manualmente' : 'Calculado automáticamente'}>
                        {emp.vacation_days_total}{emp.vacation_days_override ? ' 🔒' : ' 🤖'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>{emp.vacation_days_used}</td>
                    <td style={{ textAlign: 'center' }}>
                      <strong style={{ color: emp.vacation_days_available < 3 ? 'var(--danger)' : 'var(--success)' }}>
                        {emp.vacation_days_available}
                      </strong>
                    </td>
                    <td><span className={`badge ${emp.is_active ? 'badge-approved' : 'badge-cancelled'}`}>{emp.is_active ? 'Activo' : 'Inactivo'}</span></td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-secondary btn-sm"
                          onClick={() => handleEditEmployee(emp)}
                          style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Pencil size={13} /> Editar
                        </button>
                        {emp.role !== 'supervisor' && emp.role !== 'admin' && (
                          <button className="btn btn-secondary btn-sm"
                            onClick={() => handlePromote(emp)}
                            title="Convertir en supervisor"
                            style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--primary)' }}>
                            <ShieldCheck size={13} /> Promover
                          </button>
                        )}
                        <button className="btn btn-secondary btn-sm"
                          onClick={() => handleResetPassword(emp)}
                          title="Restablecer contraseña"
                          style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--warning)' }}>
                          <KeyRound size={13} /> Contraseña
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Supervisores */}
      {!loading && tab === 'supervisors' && (
        <div className="card">
          <div className="card-header"><div><div className="card-title">Supervisores ({supervisors.length})</div><div className="card-subtitle">Los supervisores se crean promoviendo empleados desde la pestaña Empleados</div></div></div>
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Nombre</th><th>Email</th><th>Departamento</th><th>WhatsApp</th><th>Empleados</th><th>Estado</th><th></th></tr></thead>
              <tbody>
                {supervisors.map(sup => (
                  <tr key={sup.id}>
                    <td style={{ fontWeight: 500 }}>{sup.first_name} {sup.last_name}</td>
                    <td style={{ fontSize: 13, color: 'var(--gray-600)' }}>{sup.email}</td>
                    <td>{sup.department || '—'}</td>
                    <td style={{ fontSize: 13 }}>{sup.phone_whatsapp || '—'}</td>
                    <td style={{ textAlign: 'center' }}>{sup.employee_count}</td>
                    <td><span className={`badge ${sup.is_active ? 'badge-approved' : 'badge-cancelled'}`}>{sup.is_active ? 'Activo' : 'Inactivo'}</span></td>
<td></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Departamentos */}
      {!loading && tab === 'departments' && (
        <div className="card">
          <div className="card-header">
            <div><div className="card-title">Departamentos ({departments.length})</div>
              <div className="card-subtitle">Los activos aparecen en los formularios de empleados y supervisores</div></div>
          </div>
          {departments.length === 0 ? (
            <div className="empty-state"><div className="empty-state-icon">🏢</div><div className="empty-state-title">Sin departamentos</div></div>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead><tr><th>Nombre</th><th>Descripción</th><th>Estado</th><th>Creado</th><th></th></tr></thead>
                <tbody>
                  {departments.map(d => (
                    <tr key={d.id}>
                      <td style={{ fontWeight: 500 }}>{d.name}</td>
                      <td style={{ color: 'var(--gray-500)', fontSize: 13 }}>{d.description || '—'}</td>
                      <td><span className={`badge ${d.is_active ? 'badge-approved' : 'badge-cancelled'}`}>{d.is_active ? 'Activo' : 'Inactivo'}</span></td>
                      <td style={{ fontSize: 13, color: 'var(--gray-500)' }}>{format(new Date(d.created_at), 'dd/MM/yyyy')}</td>
                      <td><button className="btn btn-secondary btn-sm" onClick={() => setDeptModal(d)}>Editar</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Vacaciones */}
      {!loading && tab === 'vacations' && (
        <div className="card">
          <div className="card-header"><div className="card-title">Todas las vacaciones ({allVacations.length})</div></div>
          {allVacations.length === 0 ? (
            <div className="empty-state"><div className="empty-state-icon">🌴</div><div className="empty-state-title">Sin solicitudes</div></div>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead><tr><th>Empleado</th><th>Depto.</th><th>Desde</th><th>Hasta</th><th>Días</th><th>Estado</th><th>Solicitud</th></tr></thead>
                <tbody>
                  {allVacations.map(v => (
                    <tr key={v.id}>
                      <td style={{ fontWeight: 500 }}>{v.first_name} {v.last_name}</td>
                      <td style={{ color: 'var(--gray-500)', fontSize: 13 }}>{v.department || '—'}</td>
                      <td>
                        {v.start_date.slice(0, 10).split('-').reverse().join('/')}
                      </td>
                      <td>
                        {v.end_date.slice(0, 10).split('-').reverse().join('/')}
                      </td>
                      <td><strong>{v.total_days}</strong></td>
                      <td>{statusBadge(v.status)}</td>
                      <td style={{ fontSize: 13, color: 'var(--gray-500)' }}>{format(new Date(v.created_at), 'dd/MM/yyyy')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Acumulaciones de vacaciones ── */}
      {!loading && tab === 'accruals' && (
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">📅 Historial de acumulaciones de vacaciones ({allAccruals.length})</div>
              <div className="card-subtitle">Días generados automáticamente por aniversario laboral</div>
            </div>
          </div>
          {allAccruals.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">📅</div>
              <div className="empty-state-title">Sin acumulaciones registradas</div>
              <div style={{ fontSize: 13 }}>
                Los días se acumulan automáticamente cada año al cumplirse el aniversario del empleado.<br/>
                Puedes disparar el proceso manualmente con el botón "Procesar aniversarios ahora".
              </div>
            </div>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Empleado</th>
                    <th>Departamento</th>
                    <th>Fecha acumulación</th>
                    <th>Años cumplidos</th>
                    <th>Días agregados</th>
                    <th>Saldo al momento</th>
                    <th>Notas</th>
                  </tr>
                </thead>
                <tbody>
                  {allAccruals.map(a => (
                    <tr key={a.id}>
                      <td style={{ fontWeight: 500 }}>{a.first_name} {a.last_name}</td>
                      <td style={{ color: 'var(--gray-500)', fontSize: 13 }}>{a.department || '—'}</td>
                      <td>
                        {a.accrual_date.slice(0, 10).split('-').reverse().join('/')}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{
                          display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                          background: a.years_completed <= 5 ? '#EFF6FF' : a.years_completed <= 10 ? '#F0FDF4' : '#FFF7ED',
                          color:      a.years_completed <= 5 ? 'var(--primary)' : a.years_completed <= 10 ? 'var(--success)' : 'var(--warning)',
                        }}>
                          {a.years_completed} año{a.years_completed > 1 ? 's' : ''}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{
                          fontWeight: 700, color: 'var(--success)',
                          background: 'var(--success-light)', padding: '2px 10px', borderRadius: 12, fontSize: 13,
                        }}>
                          +{a.days_added}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center', fontWeight: 500 }}>{a.days_balance} días</td>
                      <td style={{ fontSize: 12, color: 'var(--gray-500)', maxWidth: 200 }} className="truncate">{a.notes || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Leyenda de rangos */}
          <div style={{ marginTop: 16, padding: '12px 16px', background: 'var(--gray-50)', borderRadius: 8, display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 12, color: 'var(--gray-600)', fontWeight: 500 }}>Escala:</div>
            {[
              { icon: '🌱', label: '1-5 años', days: '12 días', color: 'var(--primary)' },
              { icon: '🌳', label: '6-10 años', days: '18 días', color: 'var(--success)' },
              { icon: '🏆', label: '+10 años', days: '30 días', color: 'var(--warning)' },
            ].map(r => (
              <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                <span>{r.icon}</span>
                <span style={{ color: 'var(--gray-600)' }}>{r.label}:</span>
                <strong style={{ color: r.color }}>{r.days}</strong>
              </div>
            ))}
          </div>
        </div>
      )}
      <TodayAbsences />

      {empModal  && <EmployeeModal   employee={empModal  === 'create' ? null : empModal}  supervisors={supervisors} departments={activeDepts} onClose={() => setEmpModal(null)}  onDone={loadData} />}
      {deptModal && <DepartmentModal dept={deptModal === 'create' ? null : deptModal}                              onClose={() => setDeptModal(null)} onDone={loadData} />}
    </Layout>
  );
}
